import { useMemo, useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { SelectButton } from 'primereact/selectbutton';
import { Divider } from 'primereact/divider';
import { SERVICE_LOCATIONS_MAP } from '../../../utils/serviceLocations';
import ProfileService from '../../../services/agentProfile/ProfileService';

// `showAccountVisibility` is opt-in: the public/private switch belongs to the
// lending network profile flow (create + edit), not to any other consumer of
// this step.
const BasicInfoStep = ({ data, onUpdate, showAccountVisibility = false }) => {
    const [lenderCompanies, setLenderCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    /* Lending Network privacy. Self-managing (P19 auto-save) rather than part
       of the wizard's save, because the answer depends on server state the
       form doesn't hold: a company employee or agent cannot set these at all —
       their company admin owns them — and even a direct user's choice can be
       clamped by the tenant ceiling. Rendering an editable control that the
       server would ignore is worse than rendering none. */
    const [privacy, setPrivacy] = useState(null);
    const [loadingPrivacy, setLoadingPrivacy] = useState(false);
    const [savingPrivacy, setSavingPrivacy] = useState(false);
    const [privacyError, setPrivacyError] = useState(null);

    useEffect(() => {
        setLoadingCompanies(true);
        ProfileService.getLenderCompanies()
            .then((res) => {
                if (res.success && Array.isArray(res.data)) {
                    setLenderCompanies(res.data.map((name) => ({ label: name, value: name })));
                }
            })
            .catch(() => {})
            .finally(() => setLoadingCompanies(false));
    }, []);

    useEffect(() => {
        if (!showAccountVisibility) return;
        setLoadingPrivacy(true);
        ProfileService.getPrivacy()
            .then((res) => { if (res.success) setPrivacy(res.data); })
            .catch((e) => setPrivacyError(e?.message || 'Could not load privacy settings'))
            .finally(() => setLoadingPrivacy(false));
    }, [showAccountVisibility]);

    /**
     * Auto-save a privacy change: optimistic update, rollback on failure.
     * Also mirrors the resulting post level into the wizard's
     * `is_private_account` so a later profile save can't overwrite what was
     * just set here — the two are two names for the same decision.
     */
    const persistPrivacy = (patch) => {
        if (!privacy || privacy.can_edit === false) return;

        const previous = privacy;
        const nextProfile = patch.profile ?? privacy.profile_visibility_preference;
        const nextPost = patch.post ?? privacy.post_visibility_preference;

        setPrivacy({
            ...privacy,
            profile_visibility_preference: nextProfile,
            post_visibility_preference: nextPost,
        });
        setPrivacyError(null);
        setSavingPrivacy(true);

        ProfileService.updatePrivacy(nextProfile, nextPost)
            .then((res) => {
                if (!res.success) throw new Error(res.message);
                setPrivacy((p) => ({
                    ...p,
                    profile_visibility_preference: res.data.profile_visibility_preference,
                    post_visibility_preference: res.data.post_visibility_preference,
                    effective: res.data.effective,
                    is_private_account: res.data.is_private_account,
                }));
                onUpdate({ is_private_account: !!res.data.is_private_account });
            })
            .catch((e) => {
                setPrivacy(previous);
                setPrivacyError(
                    e?.response?.data?.message || e?.message || 'Could not save your change',
                );
            })
            .finally(() => setSavingPrivacy(false));
    };

    const handleChange = (field, value) => {
        onUpdate({ [field]: value });
    };

    // Product types configuration
    const LOAN_PRODUCTS = [
        { value: 'home_loan', label: 'Home Loan', icon: 'pi-home', color: 'blue' },
        { value: 'personal_loan', label: 'Personal Loan', icon: 'pi-user', color: 'purple' },
        { value: 'business_loan', label: 'Business Loan', icon: 'pi-briefcase', color: 'indigo' },
        { value: 'car_loan', label: 'Car Loan', icon: 'pi-car', color: 'teal' },
        { value: 'education_loan', label: 'Education Loan', icon: 'pi-book', color: 'orange' },
        { value: 'gold_loan', label: 'Gold Loan', icon: 'pi-star', color: 'yellow' },
        { value: 'property_loan', label: 'Loan Against Property', icon: 'pi-building', color: 'pink' },
        { value: 'credit_card', label: 'Credit Card', icon: 'pi-credit-card', color: 'cyan' },
        { value: 'working_capital', label: 'Working Capital Loan', icon: 'pi-chart-line', color: 'green' },
        { value: 'msme_loan', label: 'MSME Loan', icon: 'pi-shop', color: 'red' },
    ];

    // Product-specific expertise mapping
    const PRODUCT_EXPERTISE_MAP = {
        home_loan: [
            { label: 'Home Loan Sales Expert', value: 'home_loan_sales', icon: 'pi-chart-line', category: 'home_loan' },
            { label: 'Home Loan Credit Analysis', value: 'home_loan_credit', icon: 'pi-credit-card', category: 'home_loan' },
            { label: 'Property Valuation Expert', value: 'home_loan_valuation', icon: 'pi-building', category: 'home_loan' },
            { label: 'Home Loan Legal Expert', value: 'home_loan_legal', icon: 'pi-briefcase', category: 'home_loan' },
            { label: 'Home Loan Documentation', value: 'home_loan_documentation', icon: 'pi-file', category: 'home_loan' },
        ],
        personal_loan: [
            { label: 'Personal Loan Sales Expert', value: 'personal_loan_sales', icon: 'pi-chart-line', category: 'personal_loan' },
            { label: 'Personal Loan Credit Analysis', value: 'personal_loan_credit', icon: 'pi-credit-card', category: 'personal_loan' },
            { label: 'Income Verification Expert', value: 'personal_loan_income', icon: 'pi-check-square', category: 'personal_loan' },
            { label: 'Personal Loan Underwriting', value: 'personal_loan_underwriting', icon: 'pi-file-edit', category: 'personal_loan' },
        ],
        business_loan: [
            { label: 'Business Loan Sales Expert', value: 'business_loan_sales', icon: 'pi-chart-line', category: 'business_loan' },
            { label: 'Business Loan Credit Analysis', value: 'business_loan_credit', icon: 'pi-credit-card', category: 'business_loan' },
            { label: 'Business Financial Analysis', value: 'business_loan_financial', icon: 'pi-calculator', category: 'business_loan' },
            { label: 'MSME Loan Expert', value: 'business_loan_msme', icon: 'pi-shop', category: 'business_loan' },
            { label: 'Business Loan Legal Expert', value: 'business_loan_legal', icon: 'pi-briefcase', category: 'business_loan' },
        ],
        car_loan: [
            { label: 'Car Loan Sales Expert', value: 'car_loan_sales', icon: 'pi-chart-line', category: 'car_loan' },
            { label: 'Car Loan Credit Analysis', value: 'car_loan_credit', icon: 'pi-credit-card', category: 'car_loan' },
            { label: 'Vehicle Valuation Expert', value: 'car_loan_valuation', icon: 'pi-car', category: 'car_loan' },
        ],
        education_loan: [
            { label: 'Education Loan Sales Expert', value: 'education_loan_sales', icon: 'pi-chart-line', category: 'education_loan' },
            { label: 'Education Loan Credit Analysis', value: 'education_loan_credit', icon: 'pi-credit-card', category: 'education_loan' },
            { label: 'Education Loan Counseling', value: 'education_loan_counseling', icon: 'pi-book', category: 'education_loan' },
        ],
        gold_loan: [
            { label: 'Gold Loan Sales Expert', value: 'gold_loan_sales', icon: 'pi-chart-line', category: 'gold_loan' },
            { label: 'Gold Valuation Expert', value: 'gold_loan_valuation', icon: 'pi-star', category: 'gold_loan' },
            { label: 'Gold Loan Operations', value: 'gold_loan_operations', icon: 'pi-cog', category: 'gold_loan' },
        ],
        property_loan: [
            { label: 'LAP Sales Expert', value: 'property_loan_sales', icon: 'pi-chart-line', category: 'property_loan' },
            { label: 'LAP Credit Analysis', value: 'property_loan_credit', icon: 'pi-credit-card', category: 'property_loan' },
            { label: 'Property Valuation Expert', value: 'property_loan_valuation', icon: 'pi-building', category: 'property_loan' },
            { label: 'LAP Legal Expert', value: 'property_loan_legal', icon: 'pi-briefcase', category: 'property_loan' },
        ],
        credit_card: [
            { label: 'Credit Card Sales Expert', value: 'credit_card_sales', icon: 'pi-chart-line', category: 'credit_card' },
            { label: 'Credit Card Underwriting', value: 'credit_card_underwriting', icon: 'pi-file-edit', category: 'credit_card' },
            { label: 'Credit Card Risk Management', value: 'credit_card_risk', icon: 'pi-shield', category: 'credit_card' },
        ],
        working_capital: [
            { label: 'Working Capital Sales Expert', value: 'working_capital_sales', icon: 'pi-chart-line', category: 'working_capital' },
            { label: 'Working Capital Credit Analysis', value: 'working_capital_credit', icon: 'pi-credit-card', category: 'working_capital' },
            { label: 'Cash Flow Analysis Expert', value: 'working_capital_cashflow', icon: 'pi-money-bill', category: 'working_capital' },
        ],
        msme_loan: [
            { label: 'MSME Loan Sales Expert', value: 'msme_loan_sales', icon: 'pi-chart-line', category: 'msme_loan' },
            { label: 'MSME Loan Credit Analysis', value: 'msme_loan_credit', icon: 'pi-credit-card', category: 'msme_loan' },
            { label: 'MSME Financial Analysis', value: 'msme_loan_financial', icon: 'pi-calculator', category: 'msme_loan' },
        ],
    };

    // Indian states and major cities for service areas
    const SERVICE_LOCATIONS = [
        // Metro Cities
        { label: 'Mumbai, Maharashtra', value: 'mumbai_mh', icon: 'pi-map-marker', region: 'metro' },
        { label: 'Delhi NCR', value: 'delhi_ncr', icon: 'pi-map-marker', region: 'metro' },
        { label: 'Bangalore, Karnataka', value: 'bangalore_ka', icon: 'pi-map-marker', region: 'metro' },
        { label: 'Hyderabad, Telangana', value: 'hyderabad_tg', icon: 'pi-map-marker', region: 'metro' },
        { label: 'Chennai, Tamil Nadu', value: 'chennai_tn', icon: 'pi-map-marker', region: 'metro' },
        { label: 'Kolkata, West Bengal', value: 'kolkata_wb', icon: 'pi-map-marker', region: 'metro' },
        { label: 'Pune, Maharashtra', value: 'pune_mh', icon: 'pi-map-marker', region: 'metro' },

        // Tier 1 Cities
        { label: 'Ahmedabad, Gujarat', value: 'ahmedabad_gj', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Surat, Gujarat', value: 'surat_gj', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Jaipur, Rajasthan', value: 'jaipur_rj', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Lucknow, Uttar Pradesh', value: 'lucknow_up', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Kanpur, Uttar Pradesh', value: 'kanpur_up', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Nagpur, Maharashtra', value: 'nagpur_mh', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Indore, Madhya Pradesh', value: 'indore_mp', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Thane, Maharashtra', value: 'thane_mh', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Bhopal, Madhya Pradesh', value: 'bhopal_mp', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Visakhapatnam, Andhra Pradesh', value: 'visakhapatnam_ap', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Patna, Bihar', value: 'patna_br', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Vadodara, Gujarat', value: 'vadodara_gj', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Ghaziabad, Uttar Pradesh', value: 'ghaziabad_up', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Ludhiana, Punjab', value: 'ludhiana_pb', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Agra, Uttar Pradesh', value: 'agra_up', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Nashik, Maharashtra', value: 'nashik_mh', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Coimbatore, Tamil Nadu', value: 'coimbatore_tn', icon: 'pi-map-marker', region: 'tier1' },
        { label: 'Kochi, Kerala', value: 'kochi_kl', icon: 'pi-map-marker', region: 'tier1' },

        // State-wide options
        { label: 'All Maharashtra', value: 'state_maharashtra', icon: 'pi-globe', region: 'state' },
        { label: 'All Gujarat', value: 'state_gujarat', icon: 'pi-globe', region: 'state' },
        { label: 'All Karnataka', value: 'state_karnataka', icon: 'pi-globe', region: 'state' },
        { label: 'All Tamil Nadu', value: 'state_tamilnadu', icon: 'pi-globe', region: 'state' },
        { label: 'All Telangana', value: 'state_telangana', icon: 'pi-globe', region: 'state' },
        { label: 'All Andhra Pradesh', value: 'state_andhrapradesh', icon: 'pi-globe', region: 'state' },
        { label: 'All Uttar Pradesh', value: 'state_uttarpradesh', icon: 'pi-globe', region: 'state' },
        { label: 'All West Bengal', value: 'state_westbengal', icon: 'pi-globe', region: 'state' },
        { label: 'All Rajasthan', value: 'state_rajasthan', icon: 'pi-globe', region: 'state' },
        { label: 'All Madhya Pradesh', value: 'state_madhyapradesh', icon: 'pi-globe', region: 'state' },
        { label: 'All Punjab', value: 'state_punjab', icon: 'pi-globe', region: 'state' },
        { label: 'All Kerala', value: 'state_kerala', icon: 'pi-globe', region: 'state' },
        { label: 'All India (Pan-India)', value: 'all_india', icon: 'pi-globe', region: 'national' },
    ];

    // Professional roles configuration
    const PROFESSIONAL_ROLES = [
        { value: 'banker', label: 'Banker', icon: 'pi-building', color: 'blue' },
        { value: 'borrower', label: 'Borrower', icon: 'pi-user', color: 'purple' },
        { value: 'dsa', label: 'DSA (Direct Selling Agent)', icon: 'pi-users', color: 'indigo' },
        { value: 'lawyer', label: 'Lawyer', icon: 'pi-briefcase', color: 'teal' },
        { value: 'ca', label: 'Chartered Accountant', icon: 'pi-calculator', color: 'orange' },
        { value: 'property_consultant', label: 'Property Consultant', icon: 'pi-home', color: 'pink' },
        { value: 'insurance_advisor', label: 'Insurance Advisor', icon: 'pi-shield', color: 'cyan' },
        { value: 'broker', label: 'Broker', icon: 'pi-chart-line', color: 'green' },
    ];

    // Which loan products are relevant per role
    const ROLE_PRODUCT_MAP = {
        banker:              ['home_loan', 'personal_loan', 'business_loan', 'car_loan', 'education_loan', 'gold_loan', 'property_loan', 'credit_card', 'working_capital', 'msme_loan'],
        borrower:            ['home_loan', 'personal_loan', 'business_loan', 'car_loan', 'education_loan', 'gold_loan', 'property_loan'],
        dsa:                 ['home_loan', 'personal_loan', 'business_loan', 'car_loan', 'education_loan', 'gold_loan', 'property_loan', 'credit_card', 'working_capital', 'msme_loan'],
        broker:              ['home_loan', 'personal_loan', 'business_loan', 'car_loan', 'education_loan', 'gold_loan', 'property_loan', 'credit_card', 'working_capital', 'msme_loan'],
        lawyer:              ['home_loan', 'property_loan', 'business_loan'],
        ca:                  ['business_loan', 'msme_loan', 'working_capital', 'personal_loan', 'education_loan'],
        property_consultant: ['home_loan', 'property_loan', 'business_loan'],
        insurance_advisor:   ['personal_loan', 'home_loan', 'credit_card', 'education_loan'],
    };

    // Employment types configuration
    const EMPLOYMENT_TYPES = [
        { value: 'salaried', label: 'Salaried', icon: 'pi-briefcase', color: 'blue' },
        { value: 'self_employed', label: 'Self-Employed', icon: 'pi-user', color: 'green' },
    ];

    // Get current product expertise as array of product types
    const selectedProductTypes = data.product_expertise && Array.isArray(data.product_expertise)
        ? data.product_expertise.map(pe => pe.product_type)
        : [];

    // Dynamically generate available expertise options based on selected loan products
    const availableExpertiseOptions = useMemo(() => {
        if (!selectedProductTypes || selectedProductTypes.length === 0) {
            return [];
        }

        const options = [];
        selectedProductTypes.forEach(productType => {
            if (PRODUCT_EXPERTISE_MAP[productType]) {
                options.push(...PRODUCT_EXPERTISE_MAP[productType]);
            }
        });

        return options;
    }, [selectedProductTypes]);

    const expertiseOptionTemplate = (option) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${option.icon} text-primary`}></i>
                <span>{option.label}</span>
            </div>
        );
    };

    const locationOptionTemplate = (option) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${option.icon} text-primary`}></i>
                <span>{option.label}</span>
            </div>
        );
    };

    // Handle loan product change (multiselect)
    const handleLoanProductChange = (productTypes) => {
        const newProductExpertise = productTypes.map(productType => ({
            product_type: productType,
            expertise_levels: [],
            years_of_experience: 0,
            total_deals: 0
        }));
        onUpdate({ product_expertise: newProductExpertise });
    };

    // Get current role as single object
    const currentRole = data.roles && data.roles.length > 0
        ? data.roles[0]
        : { role_type: '', is_primary: true, years_in_role: 0 };

    // Filter loan products to only those relevant for the current role
    const availableLoanProducts = useMemo(() => {
        const role = currentRole.role_type;
        if (!role || !ROLE_PRODUCT_MAP[role]) return LOAN_PRODUCTS;
        const allowed = ROLE_PRODUCT_MAP[role];
        return LOAN_PRODUCTS.filter(p => allowed.includes(p.value));
    }, [currentRole.role_type]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle role change — also drops any selected products/expertise no longer valid for new role
    const handleRoleChange = (roleType) => {
        const newRoles = [{ role_type: roleType, is_primary: true, years_in_role: 0 }];
        const allowed = ROLE_PRODUCT_MAP[roleType] || [];
        const filteredProducts = (data.product_expertise || []).filter(pe => allowed.includes(pe.product_type));
        const remainingTypes = filteredProducts.map(pe => pe.product_type);
        const validExpertise = remainingTypes.flatMap(pt => (PRODUCT_EXPERTISE_MAP[pt] || []).map(e => e.value));
        const filteredExpertise = (Array.isArray(data.expertise) ? data.expertise : []).filter(e => validExpertise.includes(e));
        onUpdate({ roles: newRoles, product_expertise: filteredProducts, expertise: filteredExpertise });
    };

    // Handle area of expertise change (multiselect stored as array)
    const handleAreaOfExpertiseChange = (values) => {
        onUpdate({ expertise: values });
    };

    // Handle employment type change (multiselect)
    const handleEmploymentTypeChange = (values) => {
        onUpdate({ employment_types: values });
    };

    // Handle service areas (locations) change
    const handleServiceAreasChange = (values) => {
        onUpdate({ service_areas: values });
    };

    // Sanitize service_areas: filter out values that don't match any option
    // This handles cases where imported data has different format (e.g., raw city names)
    const validServiceLocationValues = SERVICE_LOCATIONS.map(loc => loc.value);
    const sanitizedServiceAreas = useMemo(() => {
        const areas = Array.isArray(data.service_areas) ? data.service_areas : [];
        return areas.filter(area => validServiceLocationValues.includes(area));
    }, [data.service_areas]);

    // Custom chip template for service areas to ensure labels always display
    const selectedLocationTemplate = (value) => {
        const option = SERVICE_LOCATIONS.find(loc => loc.value === value);
        if (option) {
            return (
                <div className="flex align-items-center gap-1">
                    <i className={`pi ${option.icon} text-xs`}></i>
                    <span>{option.label}</span>
                </div>
            );
        }
        // Fallback: use the map for readable name, or show raw value
        return <span>{SERVICE_LOCATIONS_MAP[value] || value}</span>;
    };

    const selectedRole = PROFESSIONAL_ROLES.find(r => r.value === currentRole.role_type);

    // Item template for employment types
    const employmentTypeTemplate = (option) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${option.icon} text-${option.color}-500`}></i>
                <span>{option.label}</span>
            </div>
        );
    };

    return (
        <div className="grid">
            <div className="col-12">
                <h5 className="mt-0">Basic Information</h5>
                <p className="text-600">Let's start with your professional role and basic profile information</p>
            </div>

            {/* Professional Role & Employment - FIRST SECTION */}
            <div className="col-12">
                <Divider align="left">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user text-primary text-xl"></i>
                        <span className="font-semibold">Professional Role & Employment</span>
                    </div>
                </Divider>
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="role" className="block mb-2 font-medium">
                    <i className="pi pi-briefcase mr-2 text-primary"></i>
                    Professional Role <span className="text-red-500">*</span>
                </label>
                <Dropdown
                    id="role"
                    value={currentRole.role_type}
                    options={PROFESSIONAL_ROLES}
                    onChange={(e) => handleRoleChange(e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select your professional role"
                    className="w-full"
                    itemTemplate={(option) => (
                        <div className="flex align-items-center gap-2">
                            <i className={`pi ${option.icon} text-${option.color}-500`}></i>
                            <span>{option.label}</span>
                        </div>
                    )}
                    valueTemplate={() => {
                        if (selectedRole) {
                            return (
                                <div className="flex align-items-center gap-2">
                                    <i className={`pi ${selectedRole.icon} text-${selectedRole.color}-500`}></i>
                                    <span>{selectedRole.label}</span>
                                </div>
                            );
                        }
                        return <span>Select your role</span>;
                    }}
                />
                <small className="text-500">Choose your primary professional role</small>
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="employment_type" className="block mb-2 font-medium">
                    <i className="pi pi-id-card mr-2 text-primary"></i>
                    Employment Type (Select Multiple) <span className="text-red-500">*</span>
                </label>
                <MultiSelect
                    id="employment_type"
                    value={Array.isArray(data.employment_types) ? data.employment_types : []}
                    options={EMPLOYMENT_TYPES}
                    onChange={(e) => handleEmploymentTypeChange(e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select employment type(s)"
                    className="w-full"
                    display="chip"
                    itemTemplate={employmentTypeTemplate}
                />
                <small className="text-500">Select Salaried, Self-Employed, or both</small>
            </div>

            {/* Personal Information - SECOND SECTION */}
            <div className="col-12">
                <Divider align="left">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-id-card text-primary text-xl"></i>
                        <span className="font-semibold">Personal Information</span>
                    </div>
                </Divider>
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="full_name" className="block mb-2">Full Name <span className="text-red-500">*</span></label>
                <InputText
                    id="full_name"
                    value={data.full_name || ''}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    placeholder="e.g., Rahul Sharma"
                    className="w-full"
                />
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="email" className="block mb-2">Email <span className="text-red-500">*</span></label>
                <InputText
                    id="email"
                    type="email"
                    value={data.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="e.g., rahul@example.com"
                    className="w-full"
                />
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="phone" className="block mb-2">Phone Number <span className="text-red-500">*</span></label>
                <InputText
                    id="phone"
                    value={data.phone || ''}
                    onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleChange('phone', digits);
                    }}
                    keyfilter="int"
                    maxLength={10}
                    placeholder="e.g., 9876543210"
                    className="w-full"
                />
                <small className="text-500">Enter 10-digit mobile number</small>
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="experience_years" className="block mb-2">
                    Years of Experience <span className="text-red-500">*</span>
                </label>
                <InputNumber
                    id="experience_years"
                    value={data.experience_years ?? 0}
                    onValueChange={(e) => handleChange('experience_years', e.value)}
                    min={0}
                    max={50}
                    minFractionDigits={0}
                    maxFractionDigits={1}
                    step={0.5}
                    showButtons
                    className="w-full"
                />
                <small className="text-500">e.g., 2.5 for two and a half years</small>
            </div>

            {/* Loan Products, Area of Expertise, and Service Areas */}
            <div className="col-12">
                <Divider align="left">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-briefcase text-blue-500 text-xl"></i>
                        <span className="font-semibold">Loan Products & Expertise</span>
                    </div>
                </Divider>
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="loan_product" className="block mb-2 font-medium">
                    <i className="pi pi-shopping-bag mr-2 text-primary"></i>
                    Loan Products (Select Multiple) <span className="text-red-500">*</span>
                </label>
                <MultiSelect
                    id="loan_product"
                    value={selectedProductTypes}
                    options={availableLoanProducts}
                    onChange={(e) => handleLoanProductChange(e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={currentRole.role_type ? "Select loan products you work with..." : "Select a role first..."}
                    className="w-full"
                    display="chip"
                    filter
                    filterPlaceholder="Search loan products..."
                    itemTemplate={(option) => (
                        <div className="flex align-items-center gap-2">
                            <i className={`pi ${option.icon} text-${option.color}-500`}></i>
                            <span>{option.label}</span>
                        </div>
                    )}
                    maxSelectedLabels={3}
                    selectedItemsLabel="{0} products selected"
                    disabled={!currentRole.role_type}
                />
                <small className="text-500">
                    {currentRole.role_type
                        ? `Showing ${availableLoanProducts.length} product${availableLoanProducts.length !== 1 ? 's' : ''} for: ${PROFESSIONAL_ROLES.find(r => r.value === currentRole.role_type)?.label}`
                        : 'Select your professional role above to see relevant loan products'}
                </small>
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="area_of_expertise" className="block mb-2 font-medium">
                    <i className="pi pi-star mr-2 text-primary"></i>
                    Area of Expertise (Select Multiple) <span className="text-red-500">*</span>
                </label>
                <MultiSelect
                    id="area_of_expertise"
                    value={Array.isArray(data.expertise) ? data.expertise : (data.expertise ? [data.expertise] : [])}
                    options={availableExpertiseOptions}
                    onChange={(e) => handleAreaOfExpertiseChange(e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={selectedProductTypes.length > 0 ? "Select your expertise areas..." : "First select loan products above"}
                    className="w-full"
                    display="chip"
                    filter
                    filterPlaceholder="Search expertise..."
                    itemTemplate={expertiseOptionTemplate}
                    maxSelectedLabels={3}
                    selectedItemsLabel="{0} expertise areas selected"
                    disabled={selectedProductTypes.length === 0}
                />
                <small className="text-500">
                    {selectedProductTypes.length > 0
                        ? `Showing expertise options for: ${selectedProductTypes.map(pt => LOAN_PRODUCTS.find(lp => lp.value === pt)?.label).join(', ')}`
                        : 'Select loan products first to see relevant expertise options'}
                </small>
            </div>

            {/* Service Areas / Locations */}
            <div className="col-12">
                <Divider align="left">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-map-marker text-green-500 text-xl"></i>
                        <span className="font-semibold">Service Areas (Locations)</span>
                    </div>
                </Divider>
            </div>

            <div className="col-12">
                <label htmlFor="service_areas" className="block mb-2 font-medium">
                    <i className="pi pi-map mr-2 text-primary"></i>
                    Where Can You Provide Services? (Select Multiple) <span className="text-red-500">*</span>
                </label>
                <MultiSelect
                    id="service_areas"
                    value={sanitizedServiceAreas}
                    options={SERVICE_LOCATIONS}
                    onChange={(e) => handleServiceAreasChange(e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select cities/states where you can provide services..."
                    className="w-full"
                    display="chip"
                    filter
                    filterPlaceholder="Search locations..."
                    itemTemplate={locationOptionTemplate}
                    selectedItemTemplate={selectedLocationTemplate}
                    maxSelectedLabels={5}
                    selectedItemsLabel="{0} locations selected"
                    emptyFilterMessage="No locations found"
                />
                <small className="text-500">
                    <i className="pi pi-info-circle mr-1"></i>
                    Select all cities, states, or regions where you can provide loan services. This helps clients find you based on location.
                </small>
                {Array.isArray(data.service_areas) && data.service_areas.length > 0 && (
                    <div className="mt-3 p-3 bg-green-50 border-round">
                        <div className="flex align-items-center gap-2 mb-2">
                            <i className="pi pi-check-circle text-green-500"></i>
                            <span className="font-medium text-green-900">
                                {data.service_areas.length} Location{data.service_areas.length > 1 ? 's' : ''} Selected
                            </span>
                        </div>
                        <p className="text-sm text-green-700 m-0">
                            Great! Your service coverage area is well defined.
                        </p>
                    </div>
                )}
            </div>

            {/* Companies I Work With */}
            <div className="col-12">
                <Divider align="left">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-building text-orange-500 text-xl"></i>
                        <span className="font-semibold">Companies I Work With</span>
                    </div>
                </Divider>
            </div>

            <div className="col-12">
                <label htmlFor="associated_companies" className="block mb-2 font-medium">
                    <i className="pi pi-briefcase mr-2 text-primary"></i>
                    Select Companies (Banks, NBFCs, Lenders)
                </label>
                <MultiSelect
                    id="associated_companies"
                    value={Array.isArray(data.associated_companies) ? data.associated_companies : []}
                    options={lenderCompanies}
                    onChange={(e) => handleChange('associated_companies', e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={loadingCompanies ? 'Loading companies...' : 'Select companies you work with...'}
                    className="w-full"
                    display="chip"
                    filter
                    filterPlaceholder="Search companies..."
                    maxSelectedLabels={5}
                    selectedItemsLabel="{0} companies selected"
                    disabled={loadingCompanies}
                    emptyFilterMessage="No companies found"
                />
                <small className="text-500">
                    <i className="pi pi-info-circle mr-1"></i>
                    Select all banks, NBFCs, or lenders you actively work with. This is shown on your lending network profile.
                </small>
            </div>

            <div className="col-12">
                <Divider />
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="headline" className="block mb-2">Professional Headline <span className="text-red-500">*</span></label>
                <InputText
                    id="headline"
                    value={data.headline || ''}
                    onChange={(e) => handleChange('headline', e.target.value)}
                    placeholder="e.g., Senior Loan Officer | 8 Years Experience in Home Loans"
                    className="w-full"
                />
                <small className="text-500">A short, catchy description of what you do</small>
            </div>

            <div className="col-12 md:col-6"></div>

            <div className="col-12">
                <label htmlFor="bio" className="block mb-2">Bio / About</label>
                <InputTextarea
                    id="bio"
                    value={data.bio || ''}
                    onChange={(e) => handleChange('bio', e.target.value)}
                    placeholder="Tell us about yourself, your expertise, and what you're passionate about..."
                    rows={5}
                    className="w-full"
                />
                <small className="text-500">A brief professional summary (2-3 paragraphs)</small>
            </div>

            <div className="col-12">
                <h6>Current Position</h6>
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="current_company" className="block mb-2">Current Company <span className="text-red-500">*</span></label>
                <InputText
                    id="current_company"
                    value={data.current_company || ''}
                    onChange={(e) => handleChange('current_company', e.target.value)}
                    placeholder="e.g., ABC Finance Ltd"
                    className="w-full"
                />
            </div>

            <div className="col-12 md:col-6">
                <label htmlFor="current_designation" className="block mb-2">Current Designation</label>
                <InputText
                    id="current_designation"
                    value={data.current_designation || ''}
                    onChange={(e) => handleChange('current_designation', e.target.value)}
                    placeholder="e.g., Senior Loan Officer"
                    className="w-full"
                />
            </div>

            <div className="col-12">
                <h6>Location</h6>
            </div>

            <div className="col-12 md:col-4">
                <label htmlFor="location_city" className="block mb-2">City</label>
                <InputText
                    id="location_city"
                    value={data.location_city || ''}
                    onChange={(e) => handleChange('location_city', e.target.value)}
                    placeholder="e.g., Mumbai"
                    className="w-full"
                />
            </div>

            <div className="col-12 md:col-4">
                <label htmlFor="location_state" className="block mb-2">State</label>
                <InputText
                    id="location_state"
                    value={data.location_state || ''}
                    onChange={(e) => handleChange('location_state', e.target.value)}
                    placeholder="e.g., Maharashtra"
                    className="w-full"
                />
            </div>

            <div className="col-12 md:col-4">
                <label htmlFor="location_country" className="block mb-2">Country</label>
                <InputText
                    id="location_country"
                    value={data.location_country || 'India'}
                    onChange={(e) => handleChange('location_country', e.target.value)}
                    className="w-full"
                />
            </div>

            {/* ─── Lending Network privacy ────────────────────────────────
                Two independent levels, each saved as you change it:

                  Profile — Public: anyone can open your profile.
                            Tenant Only: only people in your organisation.
                            Private: you stay findable in search, but only
                                     accepted connections can open the profile.

                  Posts   — same three levels, applied to your feed posts and
                            comments.

                For a company employee or agent the whole panel is read-only:
                their company admin owns these settings, so an editable control
                here would be a lie. */}
            {showAccountVisibility && (
            <div className="col-12">
                <Divider />
                <h6>Lending Network Privacy</h6>

                {loadingPrivacy && (
                    <div className="surface-50 border-round border-1 surface-border p-3">
                        <span className="text-500 text-sm">
                            <i className="pi pi-spin pi-spinner mr-2" aria-hidden="true" />
                            Loading your privacy settings…
                        </span>
                    </div>
                )}

                {!loadingPrivacy && privacy && (
                    <div className="surface-50 border-round border-1 surface-border p-3">
                        {privacy.can_edit === false && (
                            <div className="flex align-items-start gap-2 mb-3 pb-3 border-bottom-1 surface-border">
                                <i className="pi pi-lock text-orange-500 mt-1" aria-hidden="true" />
                                <small className="text-600">
                                    {privacy.notice || 'Your visibility is managed by your company admin.'}
                                </small>
                            </div>
                        )}

                        {/* Profile visibility */}
                        <div className="flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
                            <div className="flex align-items-center gap-3">
                                <i className="pi pi-id-card text-500" aria-hidden="true" />
                                <div>
                                    <div className="font-medium text-900 text-sm">Who can view my profile</div>
                                    <small className="text-500">
                                        Private keeps you findable — people send a connection request to view it.
                                    </small>
                                </div>
                            </div>
                            <SelectButton
                                value={privacy.profile_visibility_preference}
                                options={privacy.options || []}
                                optionLabel="label"
                                optionValue="value"
                                disabled={savingPrivacy || privacy.can_edit === false}
                                onChange={(e) => {
                                    if (!e.value || e.value === privacy.profile_visibility_preference) return;
                                    persistPrivacy({ profile: e.value });
                                }}
                                aria-label="Who can view my profile"
                            />
                        </div>

                        {/* Post visibility */}
                        <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
                            <div className="flex align-items-center gap-3">
                                <i className="pi pi-comments text-500" aria-hidden="true" />
                                <div>
                                    <div className="font-medium text-900 text-sm">Who can see my posts</div>
                                    <small className="text-500">
                                        Applies to your feed posts and your comments.
                                    </small>
                                </div>
                            </div>
                            <SelectButton
                                value={privacy.post_visibility_preference}
                                options={privacy.options || []}
                                optionLabel="label"
                                optionValue="value"
                                disabled={savingPrivacy || privacy.can_edit === false}
                                onChange={(e) => {
                                    if (!e.value || e.value === privacy.post_visibility_preference) return;
                                    persistPrivacy({ post: e.value });
                                }}
                                aria-label="Who can see my posts"
                            />
                        </div>

                        {/* The server's verdict, when it differs from the choice —
                            a tenant admin's ceiling can clamp it, and a setting
                            that silently does something else reads as broken. */}
                        {privacy.effective && (
                            privacy.effective.profile_visibility !== privacy.profile_visibility_preference ||
                            privacy.effective.post_visibility !== privacy.post_visibility_preference
                        ) && (
                            <div className="flex align-items-start gap-2 mt-3 pt-3 border-top-1 surface-border">
                                <i className="pi pi-info-circle text-orange-500 mt-1" aria-hidden="true" />
                                <small className="text-600">
                                    Your organisation limits how far profiles and posts travel, so these
                                    currently apply as <strong>{privacy.effective.profile_visibility}</strong> (profile)
                                    and <strong>{privacy.effective.post_visibility}</strong> (posts).
                                    Your choice is kept and restored if that limit is lifted.
                                </small>
                            </div>
                        )}

                        {privacyError && (
                            <small className="p-error block mt-3" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                {privacyError}
                            </small>
                        )}
                    </div>
                )}

                {!loadingPrivacy && !privacy && privacyError && (
                    <small className="p-error block" role="alert">
                        <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                        {privacyError}
                    </small>
                )}
            </div>
            )}
        </div>
    );
};

export default BasicInfoStep;
